import React, { createContext, useContext, useState, ReactNode } from 'react';
import { EqualisationSection } from 'further-types/lp/equalisation';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { CalendarDay } from 'further-ui/utils';

interface EqualisationContextType {
  baseCurrency: CurrencyCode;
  currentSection: EqualisationSection;
  setCurrentSection: (section: EqualisationSection) => void;
  sections: Array<EqualisationSection>;
  nextSection?: EqualisationSection;
  previousSection?: EqualisationSection;
  goToNextSection: () => void;
  goToPreviousSection: () => void;
  closeDate?: CalendarDay;
  commitments: Array<{
    _id: string;
    isBalancingEntity: boolean;
    totalCommitmentLcy: number;
  }>;
}

const EqualisationContext = createContext<EqualisationContextType | null>(null);

interface EqualisationProviderProps {
  children: ReactNode;
  initialSection: EqualisationSection;
  sections: Array<EqualisationSection>;
  baseCurrency: CurrencyCode;
  closeDate?: CalendarDay;
  commitments: Array<{
    _id: string;
    isBalancingEntity: boolean;
    totalCommitmentLcy: number;
  }>;
}

export const EqualisationProvider: React.FC<EqualisationProviderProps> = ({
  children,
  initialSection,
  sections,
  baseCurrency,
  closeDate,
  commitments,
}) => {
  const [currentSection, setCurrentSection] =
    useState<EqualisationSection>(initialSection);

  const currentIndex = sections.indexOf(currentSection);
  const nextSection = sections[currentIndex + 1];
  const previousSection = sections[currentIndex - 1];
  const goToNextSection = () => {
    setCurrentSection(nextSection);
  };
  const goToPreviousSection = () => {
    setCurrentSection(previousSection);
  };

  return (
    <EqualisationContext.Provider
      value={{
        baseCurrency,
        closeDate,
        currentSection,
        setCurrentSection,
        sections,
        nextSection,
        previousSection,
        goToNextSection,
        goToPreviousSection,
        commitments,
      }}
    >
      {children}
    </EqualisationContext.Provider>
  );
};

export const useEqualisationSection = () => {
  const context = useContext(EqualisationContext);
  if (!context) {
    throw new Error(
      'useEqualisationSection must be used within an EqualisationProvider',
    );
  }
  return context;
};
